import { useEffect, useState } from 'react';

import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { ErrorState, LoadingState } from '@/shared/ui';

import { usePriceProtectionPolicy, useUpsertPriceProtectionPolicy } from '../../hooks';
import type { PriceProtectionPolicyRequest } from '../../model/types';
import type { ToastSeverity } from '../../lib/useToast';

interface PanelProps {
  canManage: boolean;
  onResult: (severity: ToastSeverity, message: string) => void;
}

type Fields = {
  minPrice: string;
  maxPrice: string;
  maxDecreasePct: string;
  maxIncreasePct: string;
  maxDiscountPct: string;
  suspiciousDropPct: string;
};

const EMPTY: Fields = {
  minPrice: '',
  maxPrice: '',
  maxDecreasePct: '',
  maxIncreasePct: '',
  maxDiscountPct: '',
  suspiciousDropPct: '',
};

function num(value: string): number | undefined {
  const t = value.trim();
  if (t === '') return undefined;
  const n = Number(t);
  return Number.isNaN(n) ? undefined : n;
}

/** Tenant price-protection policy — bounds + the suspicious-change action. */
export function PriceProtectionPanel({ canManage, onResult }: PanelProps) {
  const query = usePriceProtectionPolicy();
  const save = useUpsertPriceProtectionPolicy();

  const [fields, setFields] = useState<Fields>(EMPTY);
  const [suspicionAction, setSuspicionAction] = useState('REQUIRE_APPROVAL');
  const [active, setActive] = useState(true);
  const setField = (key: keyof Fields, value: string) => setFields((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    const p = query.data;
    if (!p) return;
    setFields({
      minPrice: p.minPrice != null ? String(p.minPrice) : '',
      maxPrice: p.maxPrice != null ? String(p.maxPrice) : '',
      maxDecreasePct: p.maxDecreasePct != null ? String(p.maxDecreasePct) : '',
      maxIncreasePct: p.maxIncreasePct != null ? String(p.maxIncreasePct) : '',
      maxDiscountPct: p.maxDiscountPct != null ? String(p.maxDiscountPct) : '',
      suspiciousDropPct: p.suspiciousDropPct != null ? String(p.suspiciousDropPct) : '',
    });
    setSuspicionAction(p.suspicionAction ?? 'REQUIRE_APPROVAL');
    setActive(p.active ?? true);
  }, [query.data]);

  const notConfigured = query.isError && query.error?.status === 404;
  if (query.isLoading) return <LoadingState label="Загрузка политики…" />;
  if (query.isError && !notConfigured) {
    return (
      <ErrorState
        title="Не удалось загрузить политику"
        message={query.error?.message ?? 'Произошла ошибка.'}
        onRetry={() => void query.refetch()}
      />
    );
  }

  const submit = () => {
    const body: PriceProtectionPolicyRequest = {
      minPrice: num(fields.minPrice),
      maxPrice: num(fields.maxPrice),
      maxDecreasePct: num(fields.maxDecreasePct),
      maxIncreasePct: num(fields.maxIncreasePct),
      maxDiscountPct: num(fields.maxDiscountPct),
      suspiciousDropPct: num(fields.suspiciousDropPct),
      suspicionAction,
      active,
    };
    save.mutate(body, {
      onSuccess: () => onResult('success', 'Политика защиты цен сохранена.'),
      onError: (error) => onResult('error', error.message),
    });
  };

  const numericRows: { key: keyof Fields; label: string; suffix?: string }[] = [
    { key: 'minPrice', label: 'Мин. цена, ₽' },
    { key: 'maxPrice', label: 'Макс. цена, ₽' },
    { key: 'maxDecreasePct', label: 'Макс. снижение, %' },
    { key: 'maxIncreasePct', label: 'Макс. повышение, %' },
    { key: 'maxDiscountPct', label: 'Макс. скидка, %' },
    { key: 'suspiciousDropPct', label: 'Подозрит. падение, %' },
  ];

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Защита цен
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Границы допустимых цен и реакция на подозрительные изменения.
        </Typography>
        {notConfigured ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Политика ещё не задана — заданы значения по умолчанию.
          </Alert>
        ) : null}
        <Stack spacing={2.5} sx={{ maxWidth: 640 }}>
          <Stack direction="row" flexWrap="wrap" useFlexGap gap={2}>
            {numericRows.map((row) => (
              <TextField
                key={row.key}
                label={row.label}
                type="number"
                value={fields[row.key]}
                onChange={(e) => setField(row.key, e.target.value)}
                disabled={!canManage}
                sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)' } }}
                slotProps={{ htmlInput: { step: '0.01' } }}
              />
            ))}
          </Stack>
          <TextField
            select
            label="Действие при подозрении"
            value={suspicionAction}
            onChange={(e) => setSuspicionAction(e.target.value)}
            disabled={!canManage}
            sx={{ maxWidth: 320 }}
          >
            <MenuItem value="REQUIRE_APPROVAL">Требовать утверждения</MenuItem>
            <MenuItem value="BLOCK">Блокировать</MenuItem>
          </TextField>
          <FormControlLabel
            control={
              <Switch checked={active} onChange={(e) => setActive(e.target.checked)} disabled={!canManage} />
            }
            label={active ? 'Политика включена' : 'Политика выключена'}
          />
          {canManage ? (
            <Button
              variant="contained"
              onClick={submit}
              disabled={save.isPending}
              startIcon={save.isPending ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />}
              sx={{ alignSelf: 'flex-start' }}
            >
              Сохранить
            </Button>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
}
